import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  DatePicker,
  Switch,
  Button,
  Space,
  Tag,
  Table,
  Typography,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  TruckOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import fetchData from "../../functions/fetchData";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export default function SuppliersLedger() {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    from: "",
    to: "",
    accounttype: "",
    suppliername: "",
    supplier_id: "",
    showOnlyFinalBalance: false
  });

  const accountTypes = [
    { value: 'Account Payable', label: '💰 Account Payable' },
    { value: 'Purchase', label: '🛒 Purchase' },
    { value: 'Cash', label: '💵 Cash' },
    { value: 'Expense', label: '💸 Expense' }
  ];

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      accounttype: "",
      suppliername: "",
      supplier_id: "",
      showOnlyFinalBalance: false
    });
    setSearchTerm('');
    setData(allData);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let currentY = 22;

    doc.setFontSize(18);
    doc.text('Supplier Ledger Report', 14, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.text(`Generated on: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, 14, currentY);
    currentY += 6;

    if (formData.suppliername) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Supplier: ${formData.suppliername}`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 6;
    }

    if (formData.from || formData.to) {
      doc.setFontSize(10);
      const dateRange = `Period: ${formData.from ? formatDisplayDate(formData.from) : 'Start'} to ${formData.to ? formatDisplayDate(formData.to) : 'End'}`;
      doc.text(dateRange, 14, currentY);
      currentY += 6;
    }

    const tableColumn = ["Txn ID", "Date", "A/C Type", "Description", "Debit", "Credit", "Balance"];
    const tableRows = [];

    let runningBalance = 0;
    data.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      runningBalance += credit - debit; // For suppliers: credit increases balance, debit decreases

      tableRows.push([
        row.transaction_id,
        formatDisplayDate(row.date),
        row.account_type,
        row.description,
        debit.toFixed(2),
        credit.toFixed(2),
        runningBalance.toFixed(2)
      ]);
    });

    tableRows.push([
      "TOTALS",
      "",
      "",
      "",
      totals.debit.toFixed(2),
      totals.credit.toFixed(2),
      totals.balance.toFixed(2)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 2,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      didParseCell: function (data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255];
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [100, 149, 237];
        }
      }
    });

    doc.save(`supplier_ledger_report_${fmt(new Date(), "ddMMyyyy")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportToCSV = () => {
    const headerRows = [
      { transaction_id: 'Supplier Ledger Report', date: '', supplier_name: '', account_type: '', supplier_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
      ...(formData.suppliername ? [{ transaction_id: `Supplier: ${formData.suppliername}`, date: '', supplier_name: '', account_type: '', supplier_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' }] : []),
      { transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, date: '', supplier_name: '', account_type: '', supplier_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
      { transaction_id: '', date: '', supplier_name: '', account_type: '', supplier_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
    ];

    const dataRows = data.map((row, index) => {
      let runningBalance = 0;
      data.slice(0, index + 1).forEach(r => {
        runningBalance += (Number(r.credit_amount) || 0) - (Number(r.debit_amount) || 0);
      });

      return {
        transaction_id: row.transaction_id,
        date: formatDisplayDate(row.date),
        supplier_name: row.supplier_name || 'N/A',
        account_type: row.account_type,
        supplier_id: row.supplier_id,
        description: row.description,
        debit_amount: row.debit_amount,
        credit_amount: row.credit_amount,
        running_balance: runningBalance.toFixed(2)
      };
    });

    const rows = [...headerRows, ...dataRows];
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(","),
      ...rows.map(row => headers.map(h => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `supplier_ledger_report_${fmt(new Date(), "ddMMyyyy")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const calculateTotals = (records) => {
    const credit = records.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
    const debit = records.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);
    setData(records);
    setTotals({ credit, debit, balance: credit - debit }); // For suppliers: credit - debit
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const fetched = await fetchData("supplier_ledger_entries", null, "id", {});
        setAllData(fetched);
        calculateTotals(fetched);

        const suppliersData = await fetchData("suppliers", null, "id", {});
        setSuppliers(suppliersData);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const filterData = () => {
      const { from, to, accounttype, supplier_id, showOnlyFinalBalance } = formData;
      let filtered = [...allData];

      if (searchTerm) {
        filtered = filtered.filter(r =>
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
      }

      if (accounttype) {
        filtered = filtered.filter((r) => r.account_type === accounttype);
      }

      if (supplier_id) {
        filtered = filtered.filter((r) => r.supplier_id === parseInt(supplier_id));
      }

      if (showOnlyFinalBalance) {
        // Group by supplier and calculate final balance for each
        const supplierBalances = {};
        filtered.forEach(r => {
          const supplierId = r.supplier_id;
          if (!supplierBalances[supplierId]) {
            const supplierInfo = suppliers.find(s => s.id === parseInt(supplierId));
            supplierBalances[supplierId] = {
              ...r,
              supplier_name: supplierInfo?.supplier_name || r.supplier_name || 'Unknown Supplier',
              debit_amount: 0,
              credit_amount: 0,
              final_balance: 0
            };
          }
          supplierBalances[supplierId].debit_amount += Number(r.debit_amount) || 0;
          supplierBalances[supplierId].credit_amount += Number(r.credit_amount) || 0;
        });

        filtered = Object.values(supplierBalances).map(supplier => ({
          ...supplier,
          final_balance: supplier.credit_amount - supplier.debit_amount,
          description: `Final Balance for ${supplier.supplier_name}`,
          transaction_id: 'BALANCE_SUMMARY'
        }));
      }

      calculateTotals(filtered);
    };

    filterData();
  }, [formData, allData, searchTerm]);

  const getStatusTag = (record) => {
    const balance = (Number(record.credit_amount) || 0) - (Number(record.debit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);

    if (balance <= 0) return <Tag color="success">Completed</Tag>;
    if (daysDiff > 30) return <Tag color="error">Overdue</Tag>;
    return <Tag color="warning">Pending</Tag>;
  };

  const columns = [
    {
      title: "Txn ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      sorter: (a, b) => (a.transaction_id || '').localeCompare(b.transaction_id || ''),
      render: (v) => <Text strong style={{ color: "#1677ff" }}>{v}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
      render: (v) => formatDisplayDate(v),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "A/C Type",
      dataIndex: "account_type",
      key: "account_type",
      sorter: (a, b) => (a.account_type || '').localeCompare(b.account_type || ''),
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: formData.showOnlyFinalBalance ? "Supplier Name" : "Description",
      key: "description",
      render: (_, record) => (
        formData.showOnlyFinalBalance
          ? <Text strong>{record.supplier_name || 'Unknown Supplier'}</Text>
          : record.description
      ),
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit_amount",
      align: "right",
      sorter: (a, b) => (Number(a.debit_amount) || 0) - (Number(b.debit_amount) || 0),
      render: (v) => v ? <Text style={{ color: "#cf1322" }}>฿{Number(v).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text> : '-',
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit_amount",
      align: "right",
      sorter: (a, b) => (Number(a.credit_amount) || 0) - (Number(b.credit_amount) || 0),
      render: (v) => v ? <Text style={{ color: "#389e0d" }}>฿{Number(v).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text> : '-',
    },
    {
      title: "Balance",
      key: "balance",
      align: "right",
      render: (_, record, index) => {
        let runningBalance = 0;
        if (formData.showOnlyFinalBalance) {
          runningBalance = record.final_balance || ((Number(record.credit_amount) || 0) - (Number(record.debit_amount) || 0));
        } else {
          data.slice(0, index + 1).forEach(r => {
            runningBalance += (Number(r.credit_amount) || 0) - (Number(r.debit_amount) || 0);
          });
        }
        return <Text strong style={{ color: runningBalance >= 0 ? "#1677ff" : "#d48806" }}>฿{runningBalance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>;
      },
    },
  ];

  return (
    <Layout>
      <Header title="Supplier Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ padding: 20 }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Credit"
                value={totals.credit}
                precision={2}
                prefix={<><span>฿</span> <ArrowUpOutlined /></>}
                valueStyle={{ color: "#389e0d" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Debit"
                value={totals.debit}
                precision={2}
                prefix={<><span>฿</span> <ArrowDownOutlined /></>}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Net Balance"
                value={totals.balance}
                precision={2}
                prefix="฿"
                valueStyle={{ color: totals.balance >= 0 ? "#1677ff" : "#d48806" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Records"
                value={data.length}
                prefix={<WalletOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Toolbar */}
        <Card style={{ marginTop: 16 }}>
          <Row gutter={[12, 12]} align="middle" justify="space-between">
            <Col flex="auto" style={{ minWidth: 260 }}>
              <Space>
                <Text strong style={{ fontSize: 15 }}>Supplier Ledger</Text>
                <Tag color="purple">{data.length} Records</Tag>
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <Button icon={<FilterOutlined />} type={showFilters ? "primary" : "default"} onClick={() => setShowFilters(!showFilters)}>
                  Filters
                </Button>
                <Button icon={<FilePdfOutlined />} danger onClick={exportToPDF}>PDF</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportToCSV} style={{ color: "#389e0d", borderColor: "#389e0d" }}>CSV</Button>
                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
              </Space>
            </Col>
          </Row>

          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Input
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search by Transaction ID, Description, Account Type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          {showFilters && (
            <Row gutter={[16, 16]} style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <Col xs={24} md={10}>
                <Text type="secondary" style={{ fontSize: 12 }}>Date Range</Text>
                <RangePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                  value={[formData.from ? dayjs(formData.from) : null, formData.to ? dayjs(formData.to) : null]}
                  onChange={(_, dateStrings) => setFormData({ ...formData, from: dateStrings?.[0] || "", to: dateStrings?.[1] || "" })}
                />
              </Col>
              <Col xs={24} md={7}>
                <Text type="secondary" style={{ fontSize: 12 }}>Account Type</Text>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="All Account Types"
                  value={formData.accounttype || undefined}
                  onChange={(value) => setFormData({ ...formData, accounttype: value || "" })}
                  options={accountTypes}
                />
              </Col>
              <Col xs={24} md={7}>
                <Text type="secondary" style={{ fontSize: 12 }}><TruckOutlined /> Supplier</Text>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="All Suppliers"
                  value={formData.supplier_id || undefined}
                  onChange={(value, option) => setFormData({
                    ...formData,
                    supplier_id: value || "",
                    suppliername: option?.label || "",
                  })}
                  options={suppliers.map(s => ({ value: s.id, label: s.supplier_name }))}
                  filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
              </Col>
              <Col xs={24}>
                <Space>
                  <Switch
                    checked={formData.showOnlyFinalBalance}
                    onChange={(checked) => setFormData({ ...formData, showOnlyFinalBalance: checked })}
                  />
                  <Text style={{ fontSize: 12 }} type="secondary">Show Only Final Balance — one row per supplier</Text>
                </Space>
              </Col>
            </Row>
          )}
        </Card>

        {/* Data Table */}
        <Card style={{ marginTop: 16 }}>
          <Table
            rowKey={(row, index) => row.id || index}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} records` }}
            scroll={{ x: true }}
            locale={{
              emptyText: (
                <div style={{ padding: "32px 0" }}>
                  <ExclamationCircleOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                  <div style={{ marginTop: 8, color: "#8c8c8c" }}>No supplier ledger entries found</div>
                </div>
              )
            }}
          />
        </Card>
      </div>
    </Layout>
  );
}
